import React, { useState } from 'react';
import { useProject } from '@/contexts/ProjectContext';
import { useAuth } from '@/contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CalendarIcon, Plus, User } from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { cn } from '@/lib/utils';
import { TaskStatus, TaskPriority } from '@/types';
import { toast } from 'sonner';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

interface CreateTaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string;
}

export const CreateTaskDialog: React.FC<CreateTaskDialogProps> = ({ open, onOpenChange, projectId }) => {
  const { addTask, currentProject } = useProject();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    assignedTo: 'unassigned',
    status: TaskStatus.TODO,
    priority: TaskPriority.MEDIUM,
    startDate: new Date(),
    endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days from now
    estimatedHours: 8,
    tags: ''
  });

  const mockUsers = [
    { id: '1', name: 'Admin User', avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=32&h=32&fit=crop&crop=face' },
    { id: '2', name: 'Manager User', avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b865?w=32&h=32&fit=crop&crop=face' },
    { id: '3', name: 'Collaborator User', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=32&h=32&fit=crop&crop=face' }
  ];

  const availableUsers = currentProject ? 
    mockUsers.filter(user => currentProject.members.some(member => member.userId === user.id)) : 
    mockUsers;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      toast.error('Título da tarefa é obrigatório');
      return;
    }

    if (formData.startDate >= formData.endDate) {
      toast.error('Data de início deve ser anterior à data de fim');
      return;
    }

    const tagsArray = formData.tags
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0);

    const newTask = {
      title: formData.title,
      description: formData.description,
      projectId,
      assignedTo: formData.assignedTo === 'unassigned' ? undefined : formData.assignedTo,
      createdBy: user!.id,
      status: formData.status,
      priority: formData.priority,
      startDate: formData.startDate,
      endDate: formData.endDate,
      estimatedHours: formData.estimatedHours,
      progress: 0,
      dependencies: [],
      tags: tagsArray,
      comments: []
    };

    addTask(newTask);
    toast.success('Tarefa criada com sucesso!');
    onOpenChange(false);
    // Reset form
    setFormData({
      title: '',
      description: '',
      assignedTo: 'unassigned',
      status: TaskStatus.TODO,
      priority: TaskPriority.MEDIUM,
      startDate: new Date(),
      endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
      estimatedHours: 8,
      tags: ''
    });
  };

  const handleDateChange = (field: 'startDate' | 'endDate', date: Date | undefined) => {
    if (date) {
      setFormData(prev => ({
        ...prev,
        [field]: date
      }));
    }
  };

  const getPriorityColor = (priority: TaskPriority) => {
    switch (priority) {
      case TaskPriority.URGENT: return 'text-red-600';
      case TaskPriority.HIGH: return 'text-orange-600';
      case TaskPriority.MEDIUM: return 'text-yellow-600';
      case TaskPriority.LOW: return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  const getPriorityText = (priority: TaskPriority) => {
    switch (priority) {
      case TaskPriority.URGENT: return 'Urgente';
      case TaskPriority.HIGH: return 'Alta';
      case TaskPriority.MEDIUM: return 'Média';
      case TaskPriority.LOW: return 'Baixa';
      default: return priority;
    }
  };

  const getStatusText = (status: TaskStatus) => {
    switch (status) {
      case TaskStatus.TODO: return 'A Fazer';
      case TaskStatus.IN_PROGRESS: return 'Em Andamento';
      case TaskStatus.REVIEW: return 'Em Revisão';
      case TaskStatus.DONE: return 'Concluído';
      default: return status;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5" />
            Nova Tarefa
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="title">Título da Tarefa *</Label>
            <Input
              id="title"
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Ex: Implementar sistema de login"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Descrição</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Descreva os detalhes da tarefa..."
              rows={3}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Atribuir para</Label>
              <Select value={formData.assignedTo} onValueChange={(value) => setFormData(prev => ({ ...prev, assignedTo: value }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione um usuário">
                    {formData.assignedTo && formData.assignedTo !== 'unassigned' ? (
                      <div className="flex items-center space-x-2">
                        <Avatar className="w-5 h-5">
                          <AvatarImage src={availableUsers.find(u => u.id === formData.assignedTo)?.avatar} />
                          <AvatarFallback className="text-xs">
                            {availableUsers.find(u => u.id === formData.assignedTo)?.name.split(' ').map(n => n[0]).join('')}
                          </AvatarFallback>
                        </Avatar>
                        <span>{availableUsers.find(u => u.id === formData.assignedTo)?.name}</span>
                      </div>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <User className="w-4 h-4 text-gray-400" />
                        <span>Não atribuído</span>
                      </div>
                    )}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unassigned">
                    <div className="flex items-center space-x-2">
                      <User className="w-4 h-4 text-gray-400" />
                      <span>Não atribuído</span>
                    </div>
                  </SelectItem>
                  {availableUsers.map(user => (
                    <SelectItem key={user.id} value={user.id}>
                      <div className="flex items-center space-x-2">
                        <Avatar className="w-5 h-5">
                          <AvatarImage src={user.avatar} alt={user.name} />
                          <AvatarFallback className="text-xs">
                            {user.name.split(' ').map(n => n[0]).join('')}
                          </AvatarFallback>
                        </Avatar>
                        <span>{user.name}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Status Inicial</Label>
              <Select value={formData.status} onValueChange={(value) => setFormData(prev => ({ ...prev, status: value as TaskStatus }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={TaskStatus.TODO}>{getStatusText(TaskStatus.TODO)}</SelectItem>
                  <SelectItem value={TaskStatus.IN_PROGRESS}>{getStatusText(TaskStatus.IN_PROGRESS)}</SelectItem>
                  <SelectItem value={TaskStatus.REVIEW}>{getStatusText(TaskStatus.REVIEW)}</SelectItem>
                  <SelectItem value={TaskStatus.DONE}>{getStatusText(TaskStatus.DONE)}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Prioridade</Label>
              <Select value={formData.priority} onValueChange={(value) => setFormData(prev => ({ ...prev, priority: value as TaskPriority }))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={TaskPriority.LOW}>
                    <span className={getPriorityColor(TaskPriority.LOW)}>
                      {getPriorityText(TaskPriority.LOW)}
                    </span>
                  </SelectItem>
                  <SelectItem value={TaskPriority.MEDIUM}>
                    <span className={getPriorityColor(TaskPriority.MEDIUM)}>
                      {getPriorityText(TaskPriority.MEDIUM)}
                    </span>
                  </SelectItem>
                  <SelectItem value={TaskPriority.HIGH}>
                    <span className={getPriorityColor(TaskPriority.HIGH)}>
                      {getPriorityText(TaskPriority.HIGH)}
                    </span>
                  </SelectItem>
                  <SelectItem value={TaskPriority.URGENT}>
                    <span className={getPriorityColor(TaskPriority.URGENT)}>
                      {getPriorityText(TaskPriority.URGENT)}
                    </span>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="estimatedHours">Horas Estimadas</Label>
              <Input
                id="estimatedHours"
                type="number"
                value={formData.estimatedHours}
                onChange={(e) => setFormData(prev => ({ ...prev, estimatedHours: parseInt(e.target.value) || 0 }))}
                min="1"
                max="168"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Data de Início</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !formData.startDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.startDate ? format(formData.startDate, "PPP", { locale: ptBR }) : "Selecionar data"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={formData.startDate}
                    onSelect={(date) => handleDateChange('startDate', date)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Data de Fim</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal",
                      !formData.endDate && "text-muted-foreground"
                    )}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {formData.endDate ? format(formData.endDate, "PPP", { locale: ptBR }) : "Selecionar data"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0">
                  <Calendar
                    mode="single"
                    selected={formData.endDate}
                    onSelect={(date) => handleDateChange('endDate', date)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tags">Tags</Label>
            <Input
              id="tags"
              value={formData.tags}
              onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
              placeholder="Ex: frontend, react, auth (separar por vírgula)"
            />
            <p className="text-xs text-gray-500">Separe as tags com vírgulas</p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-900 mb-2">Resumo da Tarefa</h4>
            <div className="text-sm text-blue-800 space-y-1">
              <p>• Projeto: {currentProject?.name}</p>
              <p>• Prioridade: <span className={getPriorityColor(formData.priority)}>{getPriorityText(formData.priority)}</span></p>
              <p>• Status: {getStatusText(formData.status)}</p>
              <p>• Duração estimada: {formData.estimatedHours} horas</p>
              {formData.assignedTo && formData.assignedTo !== 'unassigned' && (
                <p>• Atribuído para: {availableUsers.find(u => u.id === formData.assignedTo)?.name}</p>
              )}
            </div>
          </div>

          <div className="flex justify-end space-x-3">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" className="bg-gradient-to-r from-blue-500 to-purple-600">
              Criar Tarefa
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};